/* eslint-disable @typescript-eslint/no-explicit-any */
/** Defines a base query function for use with Redux Toolkit's `createApi` function
 * that intercepts all API calls and adds headers as needed. */
import type { BaseQueryFn } from '@reduxjs/toolkit/dist/query';
import axios from 'axios';
import type { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';

interface AxiosBaseQueryOptions {
  baseUrl: string;
  headers?: Record<string, string>;
  /** Custom response transformation function */
  transformResponse?: (response: AxiosResponse<any>) => any;
}

export const axiosBaseQuery =
  (
    {
      baseUrl = '',
      headers = {},
      /**  Default transformation function */
      transformResponse = (response) => response.data,
    }: AxiosBaseQueryOptions = { baseUrl: '' }
  ): BaseQueryFn<
    {
      url: string;
      method: AxiosRequestConfig['method'];
      data?: AxiosRequestConfig['data'];
      params?: AxiosRequestConfig['params'];
    },
    unknown,
    unknown
  > =>
  /* eslint-disable indent */
  async ({ url, method, data, params }) => {
    try {
      /** Create new Axios instance with specified base URL and credentials option */
      const axiosInstance = axios.create({
        baseURL: baseUrl,
        withCredentials: true,
        headers,
      });

      /** Make request using Axios instance */
      const result = await axiosInstance({ url, method, data, params, headers });
      const transformedResponse = transformResponse(result);

      return transformedResponse;
    } catch (axiosError) {
      /** Transform error object into standard format with status and data properties */
      const err = axiosError as AxiosError;
      return {
        error: {
          status: err.response?.status,
          data: err.response?.data || err.message,
        },
      };
    }
  };
/* eslint-enable indent */
